import React, { useMemo } from "react";
import {
  Box,
  ButtonBase,
  Skeleton,
  Paper,
  Stack,
  Typography,
  Tooltip,
  alpha,
} from "@mui/material";
import { ArrowBack, ArrowForward, CalendarMonth } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isSameDay,
  isBefore,
  isToday,
  getDate,
} from "date-fns";
import { formatSessionParticipantsLabel } from "./sessionCalendarFormat";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Full month calendar grid with a card for each day.
 * eventsForDay(day) => array of event items to show in the day cell.
 * renderEvent(event, day) => optional custom render for each event in the cell.
 */
export default function CalendarMonthGrid({
  calendarMonth,
  onMonthChange,
  onDayClick,
  eventsForDay,
  getEventColor,
  renderEvent,
  isLoading = false,
  titleFormat = "MMMM yyyy",
  emptyCellAspectRatio = "1 / 1",
  minDayHeight = 100,
  /** When false, past dates remain clickable (e.g. assign students to ongoing classes). */
  disablePastDays = true,
  isDayDisabled,
}) {
  const monthDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfMonth = getDay(start);
    const padded = Array(firstDayOfMonth)
      .fill(null)
      .concat(days);
    while (padded.length % 7 !== 0) padded.push(null);
    return padded;
  }, [calendarMonth]);

  const today = useMemo(() => new Date(), []);

  const isPast = (date) => {
    if (!date) return false;
    return isBefore(date, today) && !isSameDay(date, today);
  };

  const dayIsDisabled = (day) => {
    if (typeof isDayDisabled === "function") return Boolean(isDayDisabled(day));
    if (!disablePastDays) return false;
    return isPast(day);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: "transparent",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonth fontSize="small" color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: "-0.5px" }}>
              {format(calendarMonth, titleFormat)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={() => onMonthChange(-1)}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
              aria-label="Previous month"
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => onMonthChange(1)}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
              aria-label="Next month"
            >
              <ArrowForward fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          {WEEKDAY_LABELS.map((d) => (
            <Box key={d} sx={{ textAlign: "center", py: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                }}
              >
                {d}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 1,
            width: "100%",
          }}
        >
          {isLoading
            ? Array.from({ length: monthDays.length }, (_, idx) => (
                <Skeleton
                  key={`loading-${idx}`}
                  variant="rounded"
                  animation="wave"
                  sx={{ width: "100%", minHeight: minDayHeight, borderRadius: 2 }}
                />
              ))
            : monthDays.map((day, idx) => {
            if (!day) {
              return (
                <Box
                  key={`empty-${idx}`}
                  sx={{
                    width: "100%",
                    minHeight: minDayHeight,
                    borderRadius: 2,
                  }}
                />
              );
            }

            const disabled = dayIsDisabled(day);
            const past = isPast(day);
            const current = isToday(day);
            const dayEvents = eventsForDay ? eventsForDay(day) : [];

            const cellSx = (theme) => ({
              width: "100%",
              minHeight: minDayHeight,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              border: `1px ${disabled ? "dashed" : "solid"} ${
                current ? theme.palette.primary.main : theme.palette.divider
              }`,
              borderWidth: current ? 2 : 1,
              backgroundColor: disabled
                ? alpha(theme.palette.grey[500], 0.14)
                : past
                ? alpha(theme.palette.grey[500], 0.06)
                : current
                ? alpha(theme.palette.primary.main, 0.06)
                : "transparent",
              color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
              transition: "all 160ms ease",
              cursor: disabled ? "not-allowed" : "pointer",
              "&:hover": disabled
                ? {}
                : {
                    backgroundColor: current
                      ? alpha(theme.palette.primary.main, 0.12)
                      : past
                      ? alpha(theme.palette.grey[500], 0.1)
                      : alpha(theme.palette.primary.main, 0.04),
                  },
              overflow: "hidden",
            });

            return (
              <Box key={day.toISOString()} sx={{ width: "100%" }}>
                <Tooltip
                  title={
                    disabled
                      ? "Past date"
                      : past
                      ? `${format(day, "EEEE, MMM d")} (ongoing class dates are selectable)`
                      : format(day, "EEEE, MMM d")
                  }
                  arrow
                >
                  <ButtonBase
                    onClick={() => !disabled && onDayClick && onDayClick(day)}
                    disabled={disabled}
                    sx={cellSx}
                    aria-label={format(day, "EEEE, MMM d")}
                    focusRipple
                  >
                    <Stack sx={{ p: 0.75, flex: 1, minWidth: 0 }} spacing={0.5}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: current ? 700 : 600,
                          textDecoration: disabled ? "line-through" : "none",
                          opacity: disabled ? 0.75 : past ? 0.9 : 1,
                          flexShrink: 0,
                        }}
                      >
                        {getDate(day)}
                      </Typography>
                      {dayEvents.length > 0 && (
                        <Stack spacing={0.25} sx={{ overflow: "hidden" }}>
                          {dayEvents.slice(0, 3).map((ev, i) => {
                            const participantsLabel = formatSessionParticipantsLabel(ev);
                            return renderEvent ? (
                              <Box key={ev.id || i}>{renderEvent(ev, day, i)}</Box>
                            ) : (
                              <Stack
                                key={ev.id || i}
                                spacing={0.1}
                                sx={{
                                  fontSize: "0.7rem",
                                  bgcolor: getEventColor ? alpha(getEventColor(ev), 0.2) : "action.hover",
                                  color: "text.primary",
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 1,
                                  minWidth: 0,
                                  width: "100%",
                                }}
                              >
                                <Typography variant="caption" noWrap sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                                  {ev.title || "Event"}
                                </Typography>
                                {participantsLabel ? (
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{ fontSize: "0.62rem", lineHeight: 1.15, color: "text.secondary", fontWeight: 600 }}
                                  >
                                    {participantsLabel}
                                  </Typography>
                                ) : null}
                              </Stack>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <Typography variant="caption" color="text.secondary">
                              +{dayEvents.length - 3} more
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </ButtonBase>
                </Tooltip>
              </Box>
            );
            })}
        </Box>
      </Stack>
    </Paper>
  );
}
